// REACT
import { useState, useContext } from "react";

// CONTEXT
import { maincontext } from "../../context/maincontext";

// ASSETS
import { Button, Table, Collapse, Card, Spinner, Alert, ButtonGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FiRefreshCw } from "react-icons/fi";
import { BsCheckCircle, BsExclamationCircle, BsClock, BsExclamationTriangle } from "react-icons/bs";
import languagePack from "../../assets/languages/languagePack";
import '../../assets/styles/upload.css'

// HOOKS
import { useRead } from "../../hooks/useFetch";

export default function UploadLogs() {
    const { language } = useContext(maincontext)

    const [open, setOpen] = useState(false);
    const [fetchKey, setFetchKey] = useState(0);

    const { data: logs, isPending, error } = useRead(
        open ? `${process.env.REACT_APP_BACKEND_BASE_URL}event-history` : null,
        fetchKey
    );

    const handleToggle = () => setOpen(!open);
    const handleRefresh = () => setFetchKey(prev => prev + 1);

    const renderStatusCell = (status, errorMessage) => {
        let icon;
        let labelClass = "";

        switch (status) {
            case "Successful":
                icon = <BsCheckCircle className="text-success me-1" />;
                break;
            case "Error":
                icon = <BsExclamationCircle className="text-danger me-1" />;
                break;
            case "Conflicting":
                icon = <BsExclamationTriangle className="text-warning me-1" />;
                break;
            case "Processing":
            default:
                icon = <BsClock className="text-secondary me-1" />;
                break;
        }

        const statusLabel = (
            <span className={labelClass}>
                {icon}
                {status}
            </span>
        );

        // Only wrap in tooltip if status is "Error" and there's an error message
        if (status === "Error" && errorMessage) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{errorMessage}</Tooltip>}
                >
                    <span>{statusLabel}</span>
                </OverlayTrigger>
            );
        }

        return statusLabel;
    };

    return (
        <div className="my-5">
            <div className="d-flex justify-content-end mx-5 mb-2 gap-2">
                <ButtonGroup>
                    {open && (
                        <Button onClick={handleRefresh} variant="outline-secondary">
                            <FiRefreshCw className="me-1 refresh-icon-sm" /> {languagePack[language].uploadManager.refresh}
                        </Button>
                    )}
                    <Button
                        onClick={handleToggle}
                        aria-controls="upload-logs-table"
                        aria-expanded={open}
                        variant="secondary"
                    >
                        {open ? languagePack[language].uploadManager.hidelogs : languagePack[language].uploadManager.showlogs}
                    </Button>
                </ButtonGroup>
            </div>

            <Collapse in={open}>
                <div id="upload-logs-table" className="mt-3">
                    <Card className="mx-5">
                        <Card.Body>
                            {isPending && (
                                <div className="text-center my-4">
                                    <Spinner animation="border" />
                                </div>
                            )}

                            {/* ERROR DISPLAY */}
                            {error && (
                                <Alert variant="danger" className="mt-3">
                                    {error.includes("Please ensure the file contains all required columns")
                                        ? languagePack[language].uploadManager.noDataFound
                                        : error}
                                </Alert>
                            )}

                            {logs && (
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th>{languagePack[language].uploadManager.distributor}</th>
                                            <th>{languagePack[language].uploadManager.filename}</th>
                                            <th>{languagePack[language].uploadManager.date}</th>
                                            <th>{languagePack[language].uploadManager.status}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((log, idx) => (
                                            <tr key={log.id || idx}>
                                                <td>{log.distributor}</td>
                                                <td>{log.file_name}</td>
                                                <td>{new Date(log.timestamp).toLocaleString()}</td>
                                                <td>{renderStatusCell(log.status, log.error_message)}</td>
                                            </tr>
                                        ))}
                                        
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>
        </div>
    );
}
